import React, { useCallback, useEffect, useState } from 'react'
import { Button, Card, Col, Form, Nav, Row, Table } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import * as XLSX from 'xlsx'
import AdmissionLayout from './AdmissionLayout'
import { fetchReport, fetchYears, listApplicants } from './api/admissionApi'
import { CLASS_LABEL, STUDY_LABEL } from './fields'
import { useLoading } from '../contexts/LoadingContext'

const PAGE_SIZE = 100

const STAGES = [
  { key: '',          label: 'ทั้งหมด' },
  { key: 'matthayom', label: 'มัธยม' },
  { key: 'prathom',   label: 'อนุบาล-ประถม' },
]

const genderLabel = (g) => (g === 'm' ? 'ชาย' : g === 'f' ? 'หญิง' : '-')

export default function AdmissionList() {
  const { setLoading } = useLoading()
  const [stage, setStage] = useState('')
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  // '' = ผู้สมัครที่ยังไม่ถูกจัดเข้าห้อง (งานประจำวัน)
  // ปี พ.ศ. = ทุกคนที่สมัครในปีนั้น รวมคนที่กลายเป็นนักเรียนไปแล้ว (ใช้ดูย้อนหลัง)
  const [year, setYear] = useState('')
  const [years, setYears] = useState([])

  useEffect(() => { fetchYears().then(setYears).catch(() => {}) }, [])

  const load = useCallback(async (nextOffset, append) => {
    setLoading(true)
    try {
      if (year) {
        const res = await fetchReport(year)
        setRows(res)
        setTotal(res.length)
        setOffset(0)
        return
      }
      const res = await listApplicants({
        stage: stage || undefined,
        search: query || undefined,
        limit: PAGE_SIZE,
        offset: nextOffset,
      })
      setRows((prev) => (append ? [...prev, ...res.rows] : res.rows))
      setTotal(res.total)
      setOffset(nextOffset)
    } finally {
      setLoading(false)
    }
  }, [stage, query, year, setLoading])

  useEffect(() => { load(0, false) }, [load])

  const exportExcel = () => {
    const data = rows.map((r) => ({
      เลขประจำตัว: r.st_idnumber,
      'ชื่อ-นามสกุล': r.st_name,
      เพศ: genderLabel(r.st_gen),
      เลขบัตรประชาชน: r.st_idcard || '',
      สาย: STUDY_LABEL[r.regis_academicStudy] || '',
      ชั้น: CLASS_LABEL[r.regis_academicClass] || r.regis_academicClass || '',
      แผนการเรียน: (r.regis_academicProgram || '').toUpperCase(),
      จังหวัด: r.st_currentProvince || '',
      วันที่สมัคร: r.regis_date_text || '',
      ผู้รับสมัคร: r.regis_by || '',
    }))
    const sheet = XLSX.utils.json_to_sheet(data)
    const book = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(book, sheet, 'ผู้สมัคร')
    XLSX.writeFile(book, `ผู้สมัคร-${new Date().toISOString().slice(0, 10)}.xlsx`)
  }

  return (
    <AdmissionLayout
      title="รายชื่อผู้สมัคร"
      breadcrumb={[{ label: 'รายชื่อผู้สมัคร' }]}
      actions={
        <>
          <Button variant="outline-secondary" onClick={exportExcel} disabled={!rows.length}>
            <i className="ri-file-excel-2-line me-1" />Excel
          </Button>
          <Button as={Link} to="/admission/register" variant="primary">
            <i className="ri-user-add-line me-1" />รับสมัครใหม่
          </Button>
        </>
      }
    >
      <Card className="card-one mb-3">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={4}>
              <Nav variant="pills" className="gap-2">
                {STAGES.map((s) => (
                  <Nav.Item key={s.key || 'all'}>
                    <Nav.Link
                      active={stage === s.key}
                      disabled={Boolean(year)}
                      onClick={() => !year && setStage(s.key)}
                      role="button"
                    >
                      {s.label}
                    </Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>
            </Col>
            <Col md={3}>
              <Form.Select value={year} onChange={(e) => setYear(e.target.value)}>
                <option value="">ผู้สมัครที่ยังไม่จัดเข้าห้องเรียน</option>
                {years.map((y) => <option key={y} value={y}>ย้อนหลัง: สมัครปี {y}</option>)}
              </Form.Select>
            </Col>
            <Col md={5}>
              <Form
                className="d-flex gap-2"
                onSubmit={(e) => { e.preventDefault(); setQuery(search.trim()) }}
              >
                <Form.Control
                  placeholder={year ? 'ค้นหาได้เฉพาะโหมดผู้สมัครปัจจุบัน' : 'ค้นหาชื่อ / เลขประจำตัว / เลขบัตรประชาชน'}
                  value={search}
                  disabled={Boolean(year)}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <Button type="submit" variant="primary" disabled={Boolean(year)}>
                  <i className="ri-search-line" />
                </Button>
                {query && (
                  <Button variant="outline-secondary" onClick={() => { setSearch(''); setQuery('') }}>
                    ล้าง
                  </Button>
                )}
              </Form>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="card-one">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <Card.Title as="h6" className="mb-0">
            {year ? `ผู้สมัครปีการศึกษา ${year} (รวมคนที่เข้าเรียนแล้ว)` : 'ผู้สมัครที่ยังไม่จัดเข้าห้อง'}
          </Card.Title>
          <span className="fs-sm text-secondary">
            แสดง {rows.length} จาก {total} รายการ
          </span>
        </Card.Header>
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table className="mb-0" hover>
              <thead className="table-secondary">
                <tr>
                  <th style={{ width: 110 }}>เลขประจำตัว</th>
                  <th>ชื่อ-นามสกุล</th>
                  <th style={{ width: 70 }}>เพศ</th>
                  <th style={{ width: 130 }}>เลขบัตร ปชช.</th>
                  <th style={{ width: 100 }}>ชั้น</th>
                  <th style={{ width: 90 }}>แผน</th>
                  <th style={{ width: 110 }}>จังหวัด</th>
                  <th style={{ width: 110 }}>วันที่สมัคร</th>
                  <th style={{ width: 130 }}>ผู้รับสมัคร</th>
                  <th style={{ width: 130 }} className="text-end">จัดการ</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={10} className="text-center text-secondary py-4">ไม่พบข้อมูล</td></tr>
                )}
                {rows.map((r) => (
                  <tr key={r.st_id}>
                    <td className="fw-semibold">{r.st_idnumber}</td>
                    <td>{r.st_name}</td>
                    <td>{genderLabel(r.st_gen)}</td>
                    <td className="fs-sm">{r.st_idcard || '-'}</td>
                    <td>{CLASS_LABEL[r.regis_academicClass] || r.regis_academicClass || '-'}</td>
                    <td>{(r.regis_academicProgram || '-').toUpperCase()}</td>
                    <td className="fs-sm">{r.st_currentProvince || '-'}</td>
                    <td className="fs-sm">{r.regis_date_text || '-'}</td>
                    <td className="fs-sm">{r.regis_by || '-'}</td>
                    <td className="text-end">
                      <Button as={Link} to={`/admission/edit/${r.st_id}`} size="sm" variant="outline-primary" className="me-1">
                        <i className="ri-pencil-line" />
                      </Button>
                      <Button as={Link} to={`/admission/print/${r.st_id}`} size="sm" variant="outline-secondary">
                        <i className="ri-printer-line" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card.Body>
        {rows.length < total && (
          <Card.Footer className="text-center">
            <Button variant="outline-primary" onClick={() => load(offset + PAGE_SIZE, true)}>
              โหลดเพิ่ม ({total - rows.length} รายการที่เหลือ)
            </Button>
          </Card.Footer>
        )}
      </Card>
    </AdmissionLayout>
  )
}
